import React, { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';
import Alerts from '../../../../common/Alerts';
import Message from '../../../../common/Message';
import AppInput from '../../../../common/AppInput';
import ReCAPTCHA from '../../../../common/ReCaptcha';
import AppSelect from '../../../../common/AppSelect';
import UploadFiles from '../../../../common/UploadFiles';
import LoadingButton from '../../../../common/LoadingButton';
import AuthorizeArcoTerms from '../../../../common/AuthorizeArcoTerms';
import DialogInfoDni from '../../../../common/PreFormValidateDni/DialogInfoDni';
import MotiveTitle from '../../../Contact/Motive/MotiveTitle';
import {
  COMPLETE_FORM,
  DNI_NOT_VALID,
  RECAPTCHA_NOT_VALID,
  USER_NOT_VALID,
} from '../../../../../constants/texts';
import { getValidateDni } from '../../../../../redux/actions/ValidationDniActions';
import { createTicket } from '../../../../../redux/actions/TicketsActions';
import {
  selectStateContactForms,
  textMapper,
} from '../../../../../helpers/FormHelper';
import {
  nameLawMapper,
  arcoInfoExercises,
} from '../../../../../helpers/tickets/ArcoLawInfoHelper';
import { useArcoLawExercisesStyles } from './styles';

const Form = ({ ...props }) => {
  const { state, onData, mobile, fields, motiveId, valid, typeId } = props;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const classes = useArcoLawExercisesStyles({ mobile });
  const dispatch = useDispatch();
  const location = useLocation();
  const [openDialog, setOpenDialog] = useState(false);
  const [checked, setChecked] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessages, setAlertMessages] = useState('');
  const { validationStatus } = useSelector((state) => state.validationDniSlice);
  const { creationStatus } = useSelector((state) => state.ticketsReducer);

  const handleChange = (e) => {
    onData({ name: e.target.name, value: e.target.value });
  };

  const setCollectionFiles = async (files, fileRejections) => {
    if (fileRejections.length === 0) {
      if (files !== undefined) {
        files.forEach((file, index) => {
          const imgName = `image${index + 1}`;
          Object.assign(state, { [imgName]: file });
        });
      }
    }
  };

  const handleClick = async () => {
    const dni = state?.rut?.value;
    const serialNumber = state?.serialNumber?.value;

    try {
      if (!executeRecaptcha) {
        setAlertMessages(RECAPTCHA_NOT_VALID);
        setOpenAlert(true);
        return;
      }

      const freshToken = await executeRecaptcha('validate_dni');

      if (!freshToken) {
        setAlertMessages(RECAPTCHA_NOT_VALID);
        setOpenAlert(true);
        return;
      }

      const resultAction = await dispatch(
        getValidateDni({
          rut: dni,
          serie: serialNumber,
          reCaptchaToken: freshToken,
        }),
      );

      const response = resultAction.payload;

      if (response?.IndBloqueo === 'NO BLOQUEADO') {
        const sendData = selectStateContactForms(
          state,
          location.pathname,
          motiveId,
          typeId,
        );
        dispatch(createTicket(sendData));
      } else {
        setAlertMessages(response?.message || DNI_NOT_VALID);
        setOpenAlert(true);
      }
    } catch (error) {
      setAlertMessages(USER_NOT_VALID);
      setOpenAlert(true);
    }
  };

  const clickOpenDialog = () => setOpenDialog(true);
  const handleClose = () => setOpenDialog(false);

  return (
    <React.Fragment>
      <ReCAPTCHA />
      <Alerts
        severity="error"
        mobile={mobile}
        openAlert={openAlert}
        messages={alertMessages}
        onClose={() => setOpenAlert(false)}
      />

      <DialogInfoDni
        mobile={mobile}
        open={openDialog}
        handleClose={handleClose}
      />

      <MotiveTitle
        title={textMapper.get(motiveId).title}
        subtitle={textMapper.get(motiveId).subtitle}
        icon={textMapper.get(motiveId).icon}
        mobile={mobile}
      />

      <Typography className={classes.title}>{COMPLETE_FORM}</Typography>

      {fields.map((field, index) => (
        <React.Fragment key={index}>
          {!field.hidden && field.inputType !== 'select' && (
            <AppInput
              onChange={handleChange}
              onBlur={() => {}}
              onClick={clickOpenDialog}
              disabled={state[field.name]?.disabled || false}
              name={field.name}
              error={state[field.name]?.error || false}
              type={state[field.name]?.type || ''}
              label={state[field.name]?.label || ''}
              required={state[field.name]?.required || false}
              inputAdornment={state[field.name]?.inputAdornment || ''}
              success={state[field.name]?.success || ''}
              helperText={state[field.name]?.helper || ''}
              multiline={state[field.name]?.multiline || false}
              rows={state[field.name]?.rows || ''}
              value={state[field.name]?.value || ''}
              errorMessage={state[field.name]?.message || ''}
              info={state[field.name]?.info || false}
            />
          )}
          {field.inputType === 'select' && (
            <AppSelect
              name={field.name}
              label={state[field.name]?.label || ''}
              value={state[field.name]?.value || ''}
              options={nameLawMapper.get(field.name)}
              onChange={handleChange}
              required={state[field.name]?.required || false}
              onClick={() => {}}
              onBlur={() => {}}
            />
          )}
          {field.inputType === 'messages' && state['motiveClient']?.value && (
            <Box>
              <div className={classes.boxContainer}>
                <Message
                  message={
                    arcoInfoExercises.get(state['motiveClient']?.value)
                      .information
                  }
                  fullRounded
                  severity="info"
                />
              </div>

              {state['motiveClient']?.value === 'Derecho de rectificación' && (
                <UploadFiles
                  acceptPdf
                  mobile={mobile}
                  onFileUpload={setCollectionFiles}
                />
              )}
            </Box>
          )}
        </React.Fragment>
      ))}

      <AuthorizeArcoTerms disabled={!valid} onCheck={(e) => setChecked(e)} />

      <LoadingButton
        onClick={handleClick}
        variant="contained"
        color="primary"
        text="Enviar autorización"
        disabled={!(valid && checked)}
        loading={validationStatus === 'loading' || creationStatus === 'loading'}
      />
    </React.Fragment>
  );
};

Form.propTypes = {
  state: PropTypes.object,
  onData: PropTypes.func,
  mobile: PropTypes.bool,
  fields: PropTypes.array,
  motiveId: PropTypes.string,
  typeId: PropTypes.string,
  valid: PropTypes.bool,
  checked: PropTypes.bool,
};

export default Form;
